import { useLoadedPermission } from "@/hooks/use-permission";
import { NOTIFICATION_WRITE_ACTION } from "@/hooks/use-notifications";
import { ApiFailure } from "@/components/data-display/api-failure";
import { Bell, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DashboardNotification } from "@/hooks/use-notifications";

interface NotificationsPanelProps {
  notifications: DashboardNotification[];
  loading: boolean;
  error: string | null;
  onMarkRead: (id: string) => void;
  onRetry: () => void;
}

export function NotificationsPanel({ notifications, loading, error, onMarkRead, onRetry }: NotificationsPanelProps) {
  const { allowed: canMarkRead } = useLoadedPermission(NOTIFICATION_WRITE_ACTION.permission, NOTIFICATION_WRITE_ACTION.scope);
  if (loading) {
    return (
      <div className="flex h-full flex-col" id="notifications">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">Notifications</h2>
        <div className="flex flex-1 items-center justify-center">
          {/* role="status" makes the label an actual live status region; a bare
              <div aria-label> is not exposed to assistive technology, so the
              loading state was silent to screen readers (same fix as
              src/pages/enterprise/wallets.tsx). */}
          <div className="spinner" role="status" aria-label="Loading notifications" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col" id="notifications">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">Notifications</h2>
        <ApiFailure error={error} subject="your notifications" onRetry={onRetry} />
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex h-full flex-col" id="notifications">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">Notifications</h2>
        <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-line px-4 text-center text-sm text-muted">
          No notifications yet.
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" id="notifications">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Notifications</h2>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-ink"
          aria-label="Refresh notifications"
        >
          <RefreshCw className="size-3.5" aria-hidden />
        </button>
      </div>
      <div className="flex flex-1 flex-col gap-1.5">
        {notifications.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => canMarkRead && !n.read && onMarkRead(n.id)}
            disabled={!canMarkRead}
            className={cn(
              "flex items-start gap-3 rounded-lg border border-line bg-bg px-3 py-2 text-left transition-colors hover:border-primary hover:bg-surface",
              !n.read && "border-primary/30 bg-primary-soft/20"
            )}
          >
            <span className={cn("mt-0.5 flex-shrink-0", n.read ? "text-muted" : "text-primary")}>
              <Bell className="size-3.5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <div className={cn("truncate text-sm", n.read ? "text-muted" : "font-semibold text-ink")}>{n.title}</div>
              {n.body && <div className="mt-0.5 truncate text-xs text-muted">{n.body}</div>}
            </div>
            {!n.read && <span className="mt-1 size-1.5 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
          </button>
        ))}
      </div>
    </div>
  );
}
